import { defineStore } from 'pinia'
import { ref } from 'vue'
import { fetchUserTasks, fetchTeamTasks, fetchAllTasks } from '@/composables/services/useTasksApi'
import { useUserStore } from '@/stores/user'

// Maximum number of concurrent team-task API requests
const TEAM_FETCH_CONCURRENCY = 3

/**
 * Run an array of async tasks with a cap on how many run at once.
 * Returns results in the same order as the input array.
 */
async function runWithConcurrency<T>(
  tasks: (() => Promise<T>)[],
  concurrency: number
): Promise<T[]> {
  const results: T[] = new Array(tasks.length)
  let index = 0

  async function worker() {
    while (index < tasks.length) {
      const current = index++
      results[current] = await tasks[current]()
    }
  }

  const workers = Array.from({ length: Math.min(concurrency, tasks.length) }, worker)
  await Promise.all(workers)
  return results
}

export const useTasksStore = defineStore('tasks', () => {
  const userTasks = ref<any[]>([])
  const teamTasks = ref<any[]>([])
  const allTasks = ref<any[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)

  // In-flight promise references for deduplication — callers that arrive while a
  // fetch is already running will await the same promise instead of firing a new request.
  let _userTasksInflight: Promise<void> | null = null
  let _teamTasksInflight: Promise<void> | null = null
  let _allTasksInflight: Promise<void> | null = null

  async function loadUserTasks(userId: string) {
    if (_userTasksInflight) return _userTasksInflight

    _userTasksInflight = (async () => {
      loading.value = true
      error.value = null
      try {
        const data = await fetchUserTasks(userId)
        userTasks.value = Array.isArray(data) ? data : ((data as any)?.tasks || [])
      } catch (err) {
        error.value = err instanceof Error ? err.message : 'Failed to load user tasks'
      } finally {
        loading.value = false
        _userTasksInflight = null
      }
    })()

    return _userTasksInflight
  }

  async function _doLoadTeamTasks(): Promise<void> {
    const userStore = useUserStore()

    if (!userStore.userTeams || !Array.isArray(userStore.userTeams) || userStore.userTeams.length === 0) {
      teamTasks.value = []
      return
    }

    // Build a task factory per team and run with bounded concurrency
    const teamIds = userStore.userTeams
      .map((team: any) => team?.team_id || team)
      .filter(Boolean) as string[]

    const fetchers = teamIds.map((teamId) => async (): Promise<any[]> => {
      try {
        const data = await fetchTeamTasks(teamId)
        return Array.isArray(data) ? data : ((data as any)?.tasks || [])
      } catch (err) {
        console.error('📋 TasksStore: Failed to load tasks for team:', teamId, err)
        return []
      }
    })

    const allTeamTaskArrays = await runWithConcurrency(fetchers, TEAM_FETCH_CONCURRENCY)
    teamTasks.value = allTeamTaskArrays.flat()
  }

  async function loadTeamTasks() {
    if (_teamTasksInflight) return _teamTasksInflight

    _teamTasksInflight = (async () => {
      loading.value = true
      error.value = null
      try {
        await _doLoadTeamTasks()
      } catch (err) {
        error.value = err instanceof Error ? err.message : 'Failed to load team tasks'
        console.error('📋 TasksStore: Error in loadTeamTasks:', err)
      } finally {
        loading.value = false
        _teamTasksInflight = null
      }
    })()

    return _teamTasksInflight
  }

  async function loadAllTasks() {
    if (_allTasksInflight) return _allTasksInflight

    _allTasksInflight = (async () => {
      loading.value = true
      error.value = null
      try {
        const userStore = useUserStore()
        const orgId = userStore.userData?.object_id || ''
        const data = await fetchAllTasks(orgId)
        const tasks = Array.isArray(data) ? data : ((data as any)?.tasks || [])
        allTasks.value = tasks
      } catch (err) {
        error.value = err instanceof Error ? err.message : 'Failed to load all tasks'
        console.error('📋 TasksStore: Error in loadAllTasks:', err)
      } finally {
        loading.value = false
        _allTasksInflight = null
      }
    })()

    return _allTasksInflight
  }

  // Silent refresh methods — same deduplication, no loading spinner
  let _userTasksSilentInflight: Promise<void> | null = null
  let _teamTasksSilentInflight: Promise<void> | null = null
  let _allTasksSilentInflight: Promise<void> | null = null

  async function loadUserTasksSilently(userId: string) {
    if (_userTasksSilentInflight) return _userTasksSilentInflight

    _userTasksSilentInflight = (async () => {
      try {
        const data = await fetchUserTasks(userId)
        userTasks.value = Array.isArray(data) ? data : ((data as any)?.tasks || [])
      } catch (err) {
        console.error('📋 TasksStore: Error in loadUserTasksSilently:', err)
      } finally {
        _userTasksSilentInflight = null
      }
    })()

    return _userTasksSilentInflight
  }

  async function loadTeamTasksSilently() {
    if (_teamTasksSilentInflight) return _teamTasksSilentInflight

    _teamTasksSilentInflight = (async () => {
      try {
        await _doLoadTeamTasks()
      } catch (err) {
        console.error('📋 TasksStore: Error in loadTeamTasksSilently:', err)
      } finally {
        _teamTasksSilentInflight = null
      }
    })()

    return _teamTasksSilentInflight
  }

  async function loadAllTasksSilently() {
    if (_allTasksSilentInflight) return _allTasksSilentInflight

    _allTasksSilentInflight = (async () => {
      try {
        const userStore = useUserStore()
        const orgId = userStore.userData?.object_id || ''
        const data = await fetchAllTasks(orgId)
        const tasks = Array.isArray(data) ? data : ((data as any)?.tasks || [])
        allTasks.value = tasks
      } catch (err) {
        console.error('📋 TasksStore: Error in loadAllTasksSilently:', err)
      } finally {
        _allTasksSilentInflight = null
      }
    })()

    return _allTasksSilentInflight
  }

  return {
    userTasks,
    teamTasks,
    allTasks,
    loading,
    error,
    loadUserTasks,
    loadTeamTasks,
    loadAllTasks,
    loadUserTasksSilently,
    loadTeamTasksSilently,
    loadAllTasksSilently
  }
}) 